import { z } from "zod";

/**
 * Schemas Zod alineados con `api/app/schemas/clientes.py`.
 * El backend valida estos mismos campos — si difieren, el API devuelve 422.
 */

export const tipoClienteEnum = ["residencial", "empresarial", "dedicado"] as const;
export const estatusClienteEnum = ["activo", "suspendido", "cancelado", "prospecto"] as const;

export type TipoCliente = (typeof tipoClienteEnum)[number];
export type EstatusCliente = (typeof estatusClienteEnum)[number];

// ── RFC helper ────────────────────────────────────────────────────────
export const rfcSchema = z
  .string()
  .min(12, "RFC de 12 o 13 caracteres")
  .max(13, "RFC de 12 o 13 caracteres")
  .transform((v) => v.trim().toUpperCase())
  .refine(
    (v) => /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/.test(v),
    "Formato de RFC inválido"
  );

// ── Datos fiscales ────────────────────────────────────────────────────
export const datosFiscalesSchema = z.object({
  regimen_fiscal: z.string().max(10).optional().or(z.literal("")),
  uso_cfdi_default: z.string().max(10).optional().or(z.literal("")),
  email_fiscal: z.string().email("Email inválido").optional().or(z.literal("")),
  calle: z.string().max(200).optional().or(z.literal("")),
  numero_exterior: z.string().max(30).optional().or(z.literal("")),
  numero_interior: z.string().max(30).optional().or(z.literal("")),
  colonia: z.string().max(100).optional().or(z.literal("")),
  codigo_postal: z.string().max(10).optional().or(z.literal("")),
  municipio: z.string().max(100).optional().or(z.literal("")),
  estado: z.string().max(100).optional().or(z.literal("")),
  pais: z.string().default("MEX"),
  referencias: z.string().optional().or(z.literal("")),
});

// ── Dirección instalación ────────────────────────────────────────────
export const direccionInstalacionSchema = z.object({
  calle: z.string().max(200).optional().or(z.literal("")),
  numero_exterior: z.string().max(30).optional().or(z.literal("")),
  numero_interior: z.string().max(30).optional().or(z.literal("")),
  colonia: z.string().max(100).optional().or(z.literal("")),
  codigo_postal: z.string().max(10).optional().or(z.literal("")),
  municipio: z.string().max(100).optional().or(z.literal("")),
  estado: z.string().max(100).optional().or(z.literal("")),
  referencias: z.string().optional().or(z.literal("")),
  latitud: z.union([z.number(), z.string()]).optional(),
  longitud: z.union([z.number(), z.string()]).optional(),
});

// ── Contactos ────────────────────────────────────────────────────────
// NOTA: el campo `id` es opcional SOLO para uso local del form
// (diferencia contactos existentes vs nuevos al editar un cliente).
// No se manda al POST inicial — solo alimenta la lógica de diff en PATCH.
export const contactoSchema = z.object({
  id: z.string().uuid().optional(),
  nombre: z.string().min(2, "Nombre requerido").max(200),
  telefono: z.string().max(30).optional().or(z.literal("")),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  puesto: z.string().max(100).optional().or(z.literal("")),
  es_principal: z.boolean().default(false),
  recibe_avisos: z.boolean().default(true),
  notas: z.string().optional().or(z.literal("")),
});

// ── Cliente create ───────────────────────────────────────────────────
export const clienteCreateSchema = z.object({
  tipo: z.enum(tipoClienteEnum),
  // rc19+: sub-empresa multi-tenant. Si se omite, backend default a 1.
  empresa_id: z.number().int().positive().optional(),
  razon_social: z.string().min(2, "Mínimo 2 caracteres").max(200),
  rfc: rfcSchema,
  nombre_comercial: z.string().max(200).optional().or(z.literal("")),
  curp: z.string().length(18, "CURP de 18 caracteres").optional().or(z.literal("")),
  notas: z.string().optional().or(z.literal("")),
  notas_internas: z.string().optional().or(z.literal("")),
  tags: z.array(z.string()).default([]),
  datos_fiscales: datosFiscalesSchema.optional(),
  direccion_instalacion: direccionInstalacionSchema.optional(),
  contactos: z.array(contactoSchema).default([]),
});

export const clienteUpdateSchema = clienteCreateSchema.partial().extend({
  estatus: z.enum(estatusClienteEnum).optional(),
  motivo_baja: z.string().optional(),
});

export type ClienteCreateInput = z.infer<typeof clienteCreateSchema>;
export type ClienteUpdateInput = z.infer<typeof clienteUpdateSchema>;
export type DatosFiscalesInput = z.infer<typeof datosFiscalesSchema>;
export type DireccionInstalacionInput = z.infer<typeof direccionInstalacionSchema>;
export type ContactoInput = z.infer<typeof contactoSchema>;

// ── Response types (del backend) ──────────────────────────────────────
// v1.1.5: agregado `estado_derivado` — calculado en backend desde los
// contratos del cliente (igual que el resumen operativo del detalle).
// Permite que el listado muestre "suspendido" cuando el `estatus` manual
// del cliente sigue "activo" pero todos sus contratos están suspendidos.
export type EstadoDerivado = 'activo' | 'error' | 'suspendido' | 'cancelado' | 'sin_servicio';

export type ClienteSummary = {
  id: string;
  codigo: string;
  tipo: TipoCliente;
  // rc19+: empresa siempre presente (DEFAULT 1 NOT NULL en BD)
  empresa_id: number;
  razon_social: string;
  nombre_comercial: string | null;
  rfc: string;
  estatus: EstatusCliente;
  estado_derivado: EstadoDerivado | null;
  fecha_alta: string;
  created_at: string;
};

export type ContactoOut = {
  id: string;
  nombre: string;
  telefono: string | null;
  email: string | null;
  puesto: string | null;
  es_principal: boolean;
  recibe_avisos: boolean;
  notas: string | null;
  created_at: string;
};

export type ClienteDetail = ClienteSummary & {
  curp: string | null;
  fecha_baja: string | null;
  motivo_baja: string | null;
  notas: string | null;
  notas_internas: string | null;
  tags: string[];
  datos_fiscales: Record<string, unknown> | null;
  direccion_instalacion: Record<string, unknown> | null;
  contactos: ContactoOut[];
};

export type PagedClientes = {
  items: ClienteSummary[];
  total: number;
  page: number;
  size: number;
  pages: number;
};

// ── Resumen operativo (F14.4) ────────────────────────────────────────
export type EstadoAgregado =
  | "activo"
  | "suspendido"
  | "cancelado"
  | "sin_servicio"
  | "error";

export type ContratoMiniResumen = {
  id: string;
  codigo: string;
  estatus: string;
  exento_facturacion: boolean;
};

export type FacturaVencidaItem = {
  id: string;
  folio: string;
  contrato_id: string | null;
  fecha_emision: string;
  fecha_vencimiento: string;
  total: string;
  saldo: string;
  moneda: string;
  dias_atraso: number;
};

export type ResumenOperativo = {
  cliente_id: string;
  contratos_total: number;
  contratos_activos: number;
  contratos_suspendidos: number;
  contratos_cancelados: number;
  contratos_pendientes: number;
  contratos_error: number;
  estado_agregado: EstadoAgregado;
  contratos: ContratoMiniResumen[];
  facturas_vencidas_count: number;
  facturas_vencidas_monto: string;
  facturas_vencidas: FacturaVencidaItem[];
  saldo_pendiente_total: string;
};
